import React, { useRef, useState } from 'react';
import { Upload, Check, AlertCircle } from 'lucide-react';
import { adminUploadFile } from '../../services/api';

/**
 * MediaUploader: shows current image preview + an upload button.
 * onUploaded(url) is called after a successful upload.
 *
 * Props:
 *  - label: string
 *  - currentUrl: string | null
 *  - folder: string (subfolder under /uploads/)
 *  - onUploaded: (url) => Promise|void
 *  - testIdPrefix: string used to build data-testid values
 *  - aspect: tailwind aspect class (default aspect-[4/5])
 */
const MediaUploader = ({ label, currentUrl, folder, onUploaded, testIdPrefix, aspect = 'aspect-[4/5]' }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // 'ok' | 'error'
  const [errMsg, setErrMsg] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setStatus(null);
    setErrMsg('');
    try {
      const { url } = await adminUploadFile(file, folder);
      await Promise.resolve(onUploaded?.(url));
      setStatus('ok');
    } catch (err) {
      setStatus('error');
      setErrMsg(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="bg-[#141414] border border-[#2a2a2a] p-5 flex flex-col" data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-white text-sm tracking-widest uppercase">{label}</h4>
        {status === 'ok' && (
          <span className="text-green-400 text-xs flex items-center gap-1" data-testid={`${testIdPrefix}-success`}>
            <Check className="w-3 h-3" /> Kaydedildi
          </span>
        )}
        {status === 'error' && (
          <span className="text-red-400 text-xs flex items-center gap-1" data-testid={`${testIdPrefix}-error`}>
            <AlertCircle className="w-3 h-3" /> Hata
          </span>
        )}
      </div>

      <div className={`bg-[#0a0a0a] border border-[#2a2a2a] ${aspect} overflow-hidden mb-4 flex items-center justify-center`}>
        {currentUrl ? (
          <img
            key={currentUrl}
            src={currentUrl}
            alt={label}
            className="w-full h-full object-cover"
            data-testid={`${testIdPrefix}-preview`}
          />
        ) : (
          <span className="text-white/30 text-xs">Resim yok</span>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
        data-testid={`${testIdPrefix}-input`}
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className="w-full bg-[#b8860b] hover:bg-[#d4af37] disabled:opacity-50 text-black text-xs font-medium tracking-widest uppercase py-3 inline-flex items-center justify-center gap-2 transition-colors"
        data-testid={`${testIdPrefix}-upload-btn`}
      >
        <Upload className="w-3.5 h-3.5" />
        {busy ? 'Yükleniyor…' : 'Resmi Değiştir'}
      </button>

      {errMsg && (
        <p className="text-red-400/80 text-xs mt-2" data-testid={`${testIdPrefix}-error-text`}>{errMsg}</p>
      )}
    </div>
  );
};

export default MediaUploader;
